import React, { useState, useEffect } from "react";
import { trainingModulesAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, PlayCircle, FileText, BookOpen } from "lucide-react";

function isValidUrl(str) {
  if (!str || !str.trim()) return false;
  try {
    const url = new URL(str.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

const emptyForm = {
  title: "",
  description: "",
  video_link: "",
  document_link: "",
  is_published: false,
};

export default function TrainingPage() {
  const { isAdmin } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const res = await trainingModulesAPI.list();
      setModules(res.data);
    } catch {
      toast.error("Failed to load training materials");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.title.trim()) {
      errs.title = "Title is required";
    }
    if (formData.video_link && !isValidUrl(formData.video_link)) {
      errs.video_link = "Please enter a valid URL (e.g. https://...)";
    }
    if (formData.document_link && !isValidUrl(formData.document_link)) {
      errs.document_link = "Please enter a valid URL (e.g. https://...)";
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        video_link: formData.video_link.trim() || null,
        document_link: formData.document_link.trim() || null,
        is_published: formData.is_published,
      };
      if (editing) {
        await trainingModulesAPI.update(editing.id, payload);
        toast.success("Training item updated");
      } else {
        await trainingModulesAPI.create(payload);
        toast.success("Training item created");
      }
      setShowDialog(false);
      setEditing(null);
      setFormData(emptyForm);
      fetchModules();
    } catch {
      toast.error("Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this training item?")) return;
    try {
      await trainingModulesAPI.delete(id);
      toast.success("Training item deleted");
      fetchModules();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const openEdit = (mod) => {
    setEditing(mod);
    setFormData({
      title: mod.title || "",
      description: mod.description || "",
      video_link: mod.video_link || "",
      document_link: mod.document_link || "",
      is_published: mod.is_published || false,
    });
    setErrors({});
    setShowDialog(true);
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setErrors({});
    setShowDialog(true);
  };

  const filteredModules = modules.filter((m) =>
    m.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="training-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Training</h1>
          <p className="text-muted-foreground mt-1">
            Onboarding and training resources for your team
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="gap-2" data-testid="add-training-btn">
            <Plus className="h-4 w-4" />
            Add Training Item
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search training materials..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-training-input"
        />
      </div>

      {/* List */}
      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : filteredModules.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center text-muted-foreground gap-3">
          <BookOpen className="h-12 w-12 opacity-30" />
          <p className="text-lg font-medium">No training materials available yet.</p>
          {isAdmin && <p className="text-sm">Click &quot;Add Training Item&quot; to get started.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredModules.map((mod) => (
            <Card key={mod.id} data-testid={`training-card-${mod.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base leading-snug">{mod.title}</CardTitle>
                    {isAdmin && !mod.is_published && (
                      <span className="inline-block mt-1 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`draft-badge-${mod.id}`}>
                        Draft
                      </span>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEdit(mod)}
                        data-testid={`edit-training-${mod.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:bg-destructive/10"
                        onClick={() => handleDelete(mod.id)}
                        data-testid={`delete-training-${mod.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {mod.description && (
                  <p className="text-sm text-muted-foreground">{mod.description}</p>
                )}
                <div className="flex flex-wrap gap-2">
                {isValidUrl(mod.video_link) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => window.open(mod.video_link, "_blank", "noopener,noreferrer")}
                    aria-label={`Watch video for ${mod.title}`}
                  >
                    <PlayCircle className="h-4 w-4 text-blue-500" />
                    Watch Video
                  </Button>
                )}
                {isValidUrl(mod.document_link) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => window.open(mod.document_link, "_blank", "noopener,noreferrer")}
                    aria-label={`View document for ${mod.title}`}
                  >
                    <FileText className="h-4 w-4 text-green-500" />
                    View Document
                  </Button>
                )}
                {!isValidUrl(mod.video_link) && !isValidUrl(mod.document_link) && (
                  <p className="text-xs text-muted-foreground">No links added.</p>
                )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Training Item" : "Add Training Item"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update the training material details."
                : "Add a new training resource with a title, video link, and/or document link."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="training-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="training-title"
                placeholder="e.g. Onboarding Overview"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                data-testid="training-title-input"
              />
              {errors.title && (
                <p className="text-xs text-destructive">{errors.title}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="training-description">Description</Label>
              <Input
                id="training-description"
                placeholder="Brief description of this training material"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="training-description-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="training-video">Video Link</Label>
              <Input
                id="training-video"
                placeholder="https://youtube.com/..."
                value={formData.video_link}
                onChange={(e) => setFormData({ ...formData, video_link: e.target.value })}
                data-testid="training-video-input"
              />
              {errors.video_link && (
                <p className="text-xs text-destructive">{errors.video_link}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="training-document">Document Link</Label>
              <Input
                id="training-document"
                placeholder="https://drive.google.com/..."
                value={formData.document_link}
                onChange={(e) => setFormData({ ...formData, document_link: e.target.value })}
                data-testid="training-document-input"
              />
              {errors.document_link && (
                <p className="text-xs text-destructive">{errors.document_link}</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="training-published"
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: !!checked })}
                data-testid="training-published-checkbox"
              />
              <Label htmlFor="training-published">Publish (visible to all users)</Label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="training-submit-btn">
                {editing ? "Save Changes" : "Add Item"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
